import { useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/useT';
import { useProgress } from '@/state/progressStore';
import { useSettings } from '@/state/settingsStore';

/**
 * Profile switcher. Profiles are local and cheap — one per character is the
 * expected usage, so create/duplicate/delete all live one click away.
 */
export function ProfileMenu() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const profiles = useProgress((s) => s.profiles);
  const activeProfileId = useProgress((s) => s.activeProfileId);
  const selectProfile = useProgress((s) => s.selectProfile);
  const createProfile = useProgress((s) => s.createProfile);
  const renameProfile = useProgress((s) => s.renameProfile);
  const duplicateProfile = useProgress((s) => s.duplicateProfile);
  const deleteProfile = useProgress((s) => s.deleteProfile);
  const setActiveProfileId = useSettings((s) => s.setActiveProfileId);

  const active = profiles.find((p) => p.id === activeProfileId);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = async (id: string): Promise<void> => {
    await selectProfile(id);
    setActiveProfileId(id);
    setOpen(false);
  };

  const onCreate = async (): Promise<void> => {
    const name = window.prompt(t('profile.namePlaceholder'));
    if (!name?.trim()) return;
    await createProfile(name.trim());
    setActiveProfileId(useProgress.getState().activeProfileId);
    setOpen(false);
  };

  const onRename = async (): Promise<void> => {
    if (!active) return;
    const name = window.prompt(t('profile.rename'), active.name);
    if (!name?.trim()) return;
    await renameProfile(active.id, name.trim());
  };

  const onDuplicate = async (): Promise<void> => {
    if (!active) return;
    await duplicateProfile(active.id, t('profile.copySuffix', { name: active.name }));
    setActiveProfileId(useProgress.getState().activeProfileId);
    setOpen(false);
  };

  const onDelete = async (): Promise<void> => {
    if (!active) return;
    if (profiles.length <= 1) {
      window.alert(t('profile.deleteLast'));
      return;
    }
    if (!window.confirm(t('profile.deleteConfirm', { name: active.name }))) return;
    await deleteProfile(active.id);
    setActiveProfileId(useProgress.getState().activeProfileId);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex max-w-28 items-center gap-1 rounded-xs border border-line bg-surface-2 px-1.5 py-1 text-[12px] leading-4 text-text-dim hover:border-pip-dim hover:text-pip md:max-w-40"
      >
        <span className="truncate">{active?.name ?? t('profile.label')}</span>
        <span aria-hidden="true" className="text-text-faint">
          ▾
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-sm border border-line bg-surface shadow-lg shadow-black/40"
        >
          <ul className="max-h-64 overflow-y-auto border-b border-line">
            {profiles.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void choose(p.id)}
                  className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-[12px] leading-4 hover:bg-surface-2 ${
                    p.id === activeProfileId ? 'text-pip' : 'text-text-dim'
                  }`}
                >
                  <span aria-hidden="true" className="w-3 shrink-0">
                    {p.id === activeProfileId ? '✓' : ''}
                  </span>
                  <span className="truncate">{p.name}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-2 gap-px bg-line">
            {[
              { label: t('profile.new'), action: onCreate },
              { label: t('profile.rename'), action: onRename },
              { label: t('profile.duplicate'), action: onDuplicate },
              { label: t('profile.delete'), action: onDelete, danger: true },
            ].map((entry) => (
              <button
                key={entry.label}
                type="button"
                role="menuitem"
                onClick={() => void entry.action()}
                className={`bg-surface px-2 py-1.5 text-left text-[11px] leading-4 hover:bg-surface-2 ${
                  entry.danger ? 'text-danger' : 'text-text-dim'
                }`}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
